import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend
} from "recharts";
import { Scale, Zap, Clock, Target, Heart, DollarSign, ChevronLeft, History } from "lucide-react";

import { API } from "../lib/api";

const DIMENSIONS = [
  { id: "speed", label: "Speed", icon: Zap, color: "#3B82F6", desc: "Faster delivery timelines" },
  { id: "quality", label: "Quality", icon: Target, color: "#10B981", desc: "Higher output standards" },
  { id: "cost", label: "Cost", icon: DollarSign, color: "#F59E0B", desc: "Lower resource expenditure" },
  { id: "alignment", label: "Alignment", icon: Scale, color: "#8B5CF6", desc: "Better stakeholder consensus" },
  { id: "energy", label: "Energy", icon: Heart, color: "#EF4444", desc: "Protect team wellbeing" },
];

export default function TradeOffPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId");
  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [history, setHistory] = useState([]);
  const [rationale, setRationale] = useState("");
  const [recording, setRecording] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/tradeoffs/history`, { withCredentials: true })
      .then(({ data }) => setHistory(data)).catch(() => {});
  }, []);

  const simulate = async (dim) => {
    setSelected(dim);
    setSimulating(true);
    try {
      const { data } = await axios.post(`${API}/api/tradeoffs/simulate`,
        { optimize_for: dim, project_id: projectId }, { withCredentials: true });
      setResult(data);
    } catch (err) { console.error(err); }
    finally { setSimulating(false); }
  };

  const recordDecision = async () => {
    if (!result) return;
    setRecording(true);
    try {
      await axios.post(`${API}/api/tradeoffs/decide`, {
        project_id: projectId, optimize_for: result.optimize_for,
        weakens: result.weakens, rationale,
      }, { withCredentials: true });
      setRationale("");
      const { data } = await axios.get(`${API}/api/tradeoffs/history`, { withCredentials: true });
      setHistory(data);
    } catch (err) { console.error(err); }
    finally { setRecording(false); }
  };

  // Radar data showing dimensions
  const radarData = DIMENSIONS.map(d => ({
    dim: d.label,
    value: result?.weakens?.includes(d.id) ? 2 : d.id === result?.optimize_for ? 5 : 3.5,
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-8 animate-slide-up">
          <button onClick={() => navigate(projectId ? `/project/${projectId}` : "/dashboard")}
            className="flex items-center gap-1 text-sm font-ibm-plex text-slate-500 hover:text-slate-900 mb-4">
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Scale className="w-5 h-5 text-purple-600" />
            <h1 className="font-outfit text-3xl font-black text-slate-900">Trade-Off Engine</h1>
          </div>
          <p className="font-ibm-plex text-sm text-slate-500">
            Explore the impact of optimizing for different dimensions. Every gain has a cost.
          </p>
        </div>

        {/* Dimension Selection */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6 animate-slide-up stagger-1">
          {DIMENSIONS.map((d) => {
            const Icon = d.icon;
            const isActive = selected === d.id;
            return (
              <button key={d.id} data-testid={`tradeoff-${d.id}`} onClick={() => simulate(d.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isActive ? "border-slate-900 bg-white shadow-md" : "border-gray-200 bg-white hover:border-slate-300"
                }`}>
                <Icon className="w-5 h-5 mb-2" style={{ color: d.color }} />
                <p className="font-outfit text-sm font-bold text-slate-900">{d.label}</p>
                <p className="font-ibm-plex text-[10px] text-slate-400 mt-0.5">{d.desc}</p>
              </button>
            );
          })}
        </div>

        {/* Simulation Result */}
        {result && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6 animate-slide-up stagger-2">
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="font-outfit text-base font-bold text-slate-900 mb-2">Impact Analysis</h2>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="font-ibm-plex text-sm text-amber-800">{result.message}</p>
              </div>
              {Object.keys(result.impact || {}).length > 0 && (
                <div className="space-y-3">
                  <p className="font-ibm-plex text-xs text-slate-500 uppercase tracking-widest">Projected Pillar Impact</p>
                  {Object.entries(result.impact).map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <span className="font-outfit text-sm font-bold text-slate-900 capitalize">{k}</span>
                        <span className="font-ibm-plex text-xs text-slate-400 ml-2">({v.pillar?.replace("_", " ")})</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-outfit text-sm text-slate-500">{v.current}</span>
                        <span className="font-ibm-plex text-xs text-slate-300">&rarr;</span>
                        <span className="font-outfit text-sm font-bold text-red-600">{v.projected}</span>
                        <span className="font-ibm-plex text-xs text-red-500">({v.change})</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {/* Record Decision */}
              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="font-ibm-plex text-xs text-slate-500 uppercase tracking-widest mb-2">Record this decision</p>
                <input type="text" value={rationale} onChange={(e) => setRationale(e.target.value)}
                  placeholder="Why are you making this trade-off?"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-ibm-plex focus:outline-none focus:ring-2 focus:ring-slate-900 mb-2" />
                <button data-testid="record-decision-btn" onClick={recordDecision} disabled={recording}
                  className="bg-slate-900 text-white font-outfit font-medium px-4 py-2 rounded-lg hover:bg-slate-800 text-sm disabled:opacity-60">
                  {recording ? "Recording..." : "Record Decision"}
                </button>
              </div>
            </div>

            {/* Trade-off Radar */}
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <h2 className="font-outfit text-base font-bold text-slate-900 mb-4">Trade-Off Radar</h2>
              <ResponsiveContainer width="100%" height={280}>
                <RadarChart data={radarData}>
                  <PolarGrid stroke="#E5E7EB" />
                  <PolarAngleAxis dataKey="dim" tick={{ fontFamily: "IBM Plex Sans", fontSize: 11, fill: "#4B5563" }} />
                  <PolarRadiusAxis angle={30} domain={[0, 5]} tickCount={6} tick={{ fontSize: 9, fill: "#9CA3AF" }} />
                  <Radar name="Impact" dataKey="value" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
              <p className="font-ibm-plex text-xs text-slate-400 text-center mt-2">
                Higher = stronger. Weakened dimensions shrink inward.
              </p>
            </div>
          </div>
        )}

        {/* Decision History */}
        {history.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-6 animate-slide-up stagger-3">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-slate-500" />
              <h2 className="font-outfit text-base font-bold text-slate-900">Decision Memory</h2>
              <span className="font-ibm-plex text-xs text-slate-400">{history.length} decisions tracked</span>
            </div>
            <div className="space-y-2">
              {history.slice(0, 10).map((d) => (
                <div key={d.id} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <span className="font-outfit text-sm font-bold text-slate-900 capitalize">Optimized: {d.optimize_for}</span>
                    <span className="font-ibm-plex text-xs text-red-500 ml-2">Weakened: {d.weakens?.join(", ")}</span>
                    {d.rationale && <p className="font-ibm-plex text-xs text-slate-500 mt-0.5">&quot;{d.rationale}&quot;</p>}
                  </div>
                  <span className="font-ibm-plex text-xs text-slate-400">
                    {new Date(d.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
